import type { HttpErrorResponse } from '@angular/common/http';

import type { Scope } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import type {
  Field,
  Fieldset,
  FieldsetPositionData,
  FieldsetUpdateData,
  FieldTemplate,
} from '@core/profile-fields-types';
import type { TemplateAssignedOffer } from '@core/workflow-templates-types';

export const offerProfileFieldsScope = (offerId: number) => scopeFactory('offer', offerId);
export const jobTemplateProfileFieldsScope = (jobTemplateId: number) =>
  scopeFactory('job-template', jobTemplateId);

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Fieldset>()(
  'fieldset',
  {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const selectId = ({ id }: Fieldset) => id;
      const defaultActions = getEntityCollectionActions(adapter, selectId);

      return {
        // Settings
        fetchCollectionSuccess: (state, payload: { data: Fieldset[] }) =>
          defaultActions.fetchCollectionSuccess(state, payload),

        fetchItem: (state, payload: { data: { id: number } }) => adapter.markAsPending(state),
        fetchItemSuccess: (state, payload: { data: Fieldset }) =>
          pipeState(
            state,
            next => adapter.addOne(state, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        fetchItemFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),

        createFieldset: (state, payload: { name: string; category: string }) =>
          adapter.markAsPending(state),
        createFieldsetSuccess: (state, payload: { data: Fieldset }) =>
          pipeState(
            state,
            next => adapter.addOne(state, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        createFieldsetFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),

        updateFieldset: (state, payload: { data: FieldsetUpdateData }) =>
          adapter.markAsPending(state),
        updateFieldsetSuccess: (state, payload: { data: Fieldset }) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        updateFieldsetFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),

        deleteFieldset: (state, payload: { id: number }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),
        deleteFieldsetSuccess: (state, payload: { id: number }) =>
          adapter.removeOne(state, payload.id),
        deleteFieldsetFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, { entities: [payload.id], scope: null }),

        duplicateFieldset: (state, payload: { id: number }) => adapter.markAsPending(state),
        duplicateFieldsetSuccess: (state, payload: { data: Fieldset }) =>
          pipeState(
            state,
            next => adapter.addOne(state, payload.data),
            next => adapter.markAsLoaded(next),
          ),

        updateFieldsetsPositions: ngrxEvent<{ data: FieldsetPositionData }>(),
        updateFieldsetsPositionsSuccess: ngrxEvent<void>(),
        updateFieldsetsPositionsFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateFieldsPositions: ngrxEvent<{ fieldsetId: number; positions: number[] }>(),
        updateFieldsPositionsSuccess: ngrxEvent<void>(),
        updateFieldsPositionsFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        createField: ngrxEvent<{ fieldsetId: number; field: FieldTemplate }>(),
        createFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        createFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateField: ngrxEvent<{ fieldsetId: number; field: FieldTemplate }>(),
        updateFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        updateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        deleteField: ngrxEvent<{ fieldsetId: number; fieldId: number }>(),
        deleteFieldSuccess: ngrxEvent<void>(),
        deleteFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        fetchAssignedOffersList: ngrxEvent<{
          id: number;
          searchParams: { q: string | null; page: number; limit: number };
        }>(),
        fetchAssignedOffersListSuccess: ngrxEvent<{
          data: {
            assignedOffers: TemplateAssignedOffer[];
            meta: { limit: number; page: number; totalCount: number };
          };
        }>(),
        fetchAssignedOffersListFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
        fetchAssignedJobTemplatesList: ngrxEvent<{
          id: number;
          searchParams: { q: string | null; page: number; limit: number };
        }>(),
        fetchAssignedJobTemplatesListSuccess: ngrxEvent<{
          data: {
            assignedOfferTemplates: TemplateAssignedOffer[];
            meta: { limit: number; page: number; totalCount: number };
          };
        }>(),
        fetchAssignedJobTemplatesListFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        // Candidate
        createCandidateField: ngrxEvent<{ candidateId: number; field: Field }>(),
        createCandidateFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        createCandidateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateCandidateField: ngrxEvent<{ candidateId: number; fieldId: number; field: Field }>(),
        updateCandidateFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        updateCandidateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        deleteCandidateField: ngrxEvent<{ candidateId: number; fieldId: number }>(),
        deleteCandidateFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        deleteCandidateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        // Offer
        fetchOfferCollection: (state, payload: { offerId: number; scope: Scope }) =>
          adapter.markAsPending(state, {
            scope: payload.scope,
            entities: [],
          }),
        fetchOfferCollectionSuccess: (
          state,
          payload: { data: Fieldset[]; offerId: number; scope: Scope },
        ) =>
          pipeState(
            adapter.resetAll(state),
            next =>
              adapter.addMany(next, payload.data, {
                scope: payload.scope,
              }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope,
                entities: payload.data.map(e => e.id),
              }),
          ),

        createOfferField: ngrxEvent<{
          offerId: number;
          field: FieldTemplate;
          fieldsetId: number;
        }>(),
        createOfferFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        createOfferFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateOfferField: ngrxEvent<{
          offerId: number;
          field: FieldTemplate;
          fieldsetId: number;
        }>(),
        updateOfferFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        updateOfferFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        deleteOfferField: ngrxEvent<{ offerId: number; fieldId: number; fieldsetId: number }>(),
        deleteOfferFieldSuccess: ngrxEvent<void>(),
        deleteOfferFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        assignOfferFieldset: ngrxEvent<{ offerId: number; fieldsetId: number | string }>(),
        assignOfferFieldsetSuccess: ngrxEvent<void>(),
        assignOfferFieldsetFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateOfferFieldsPositions: ngrxEvent<{
          offerId: number;
          positions: number[];
          fieldsetId: number;
        }>(),
        updateOfferFieldsPositionsSuccess: ngrxEvent<void>(),
        updateOfferFieldsPositionsFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        saveAsTemplate: ngrxEvent<{ title: string; category: string; offerId: number }>(),
        saveAsTemplateSuccess: ngrxEvent<{ id: number }>(),
        saveAsTemplateFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        // Job template
        fetchJobTemplateCollection: (state, payload: { jobTemplateId: number; scope: Scope }) =>
          adapter.markAsPending(state, {
            scope: payload.scope,
            entities: [],
          }),
        fetchJobTemplateCollectionSuccess: (
          state,
          payload: { data: Fieldset[]; jobTemplateId: number; scope: Scope },
        ) =>
          pipeState(
            adapter.resetAll(state),
            next =>
              adapter.addMany(next, payload.data, {
                scope: payload.scope,
              }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope,
                entities: payload.data.map(e => e.id),
              }),
          ),
        fetchJobTemplateItem: (
          state,
          payload: { data: { jobTemplateId: number; fieldsetId: number; scope: Scope } },
        ) =>
          adapter.markAsPending(state, {
            scope: payload.data.scope,
            entities: [],
          }),
        fetchJobTemplateItemSuccess: (state, payload: { data: Fieldset; scope: Scope }) =>
          pipeState(
            state,
            next => adapter.addOne(state, payload.data, { scope: payload.scope }),
            next =>
              adapter.markAsLoaded(next, { scope: payload.scope, entities: [payload.data.id] }),
          ),
        fetchJobTemplateItemFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),

        createJobTemplateField: ngrxEvent<{
          jobTemplateId: number;
          field: FieldTemplate;
          fieldsetId: number;
        }>(),
        createJobTemplateFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        createJobTemplateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateJobTemplateField: ngrxEvent<{
          jobTemplateId: number;
          field: FieldTemplate;
          fieldsetId: number;
        }>(),
        updateJobTemplateFieldSuccess: ngrxEvent<{ data: FieldTemplate }>(),
        updateJobTemplateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        deleteJobTemplateField: ngrxEvent<{
          jobTemplateId: number;
          fieldId: number;
          fieldsetId: number;
        }>(),
        deleteJobTemplateFieldSuccess: ngrxEvent<void>(),
        deleteJobTemplateFieldFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        assignJobTemplateFieldset: ngrxEvent<{
          jobTemplateId: number;
          fieldsetId: number | string;
        }>(),
        assignJobTemplateFieldsetSuccess: ngrxEvent<void>(),
        assignJobTemplateFieldsetFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        updateJobTemplateFieldsPositions: ngrxEvent<{
          jobTemplateId: number;
          positions: number[];
          fieldsetId: number;
        }>(),
        updateJobTemplateFieldsPositionsSuccess: ngrxEvent<void>(),
        updateJobTemplateFieldsPositionsFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        saveAsTemplateJobTemplate: ngrxEvent<{
          title: string;
          category: string;
          jobTemplateId: number;
        }>(),
        saveAsTemplateJobTemplateSuccess: ngrxEvent<{ id: number }>(),
        saveAsTemplateJobTemplateFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);
export {
  actions as ProfileFieldsFieldsetCollectionActions,
  selectors as ProfileFieldsFieldsetCollectionSelectors,
  reducer as ProfileFieldsFieldsetCollectionReducer,
  namespace as ProfileFieldsFieldsetCollectionNamespace,
};
